import type { MediaUploadContext } from '@mtng-reports/models';

type UploadedReference = { id: string; sourceUrl?: string; source_url?: string };

export interface EnsureReportMediaDeps {
  checkConnection(): Promise<boolean>;
  upload(dataUrl: string, context: MediaUploadContext): Promise<UploadedReference>;
}

export interface ReportMediaFailure {
  blockIndex: number;
  error: unknown;
}

type GalleryImage = Record<string, unknown> & { id?: unknown; image?: unknown; provider?: unknown; source_url?: unknown };

function hasUploadedMedia(image: GalleryImage): boolean {
  return !!image.id
    && !String(image.id).startsWith('auto-')
    && (image.provider === 'r2' || !!image.source_url);
}

/**
 * Makes every gallery image in the report reference stored media before `storeReport`.
 * Firestore is the only report copy, and `sanitizeDataForFirestore` drops base64 images,
 * so a fallback upload must be written back onto the image object itself.
 */
export async function ensureReportMedia(
  blocks: unknown[],
  context: MediaUploadContext,
  deps: EnsureReportMediaDeps,
): Promise<ReportMediaFailure | null> {
  for (const [blockIndex, block] of blocks.entries()) {
    const gallery = block as { type?: unknown; arr?: unknown };
    if (gallery?.type !== 'gallery' || !Array.isArray(gallery.arr)) continue;
    try {
      for (const image of gallery.arr as GalleryImage[]) {
        if (!image || hasUploadedMedia(image)) continue;
        const imageValue = typeof image.image === 'string' ? image.image : '';
        if (imageValue.startsWith('http://') || imageValue.startsWith('https://')) continue;

        if (!(await deps.checkConnection())) {
          throw new Error('Connection check failed - unable to reach server for image upload');
        }
        if (!imageValue.startsWith('data:')) {
          throw new Error('Missing base64 data for image upload');
        }

        const media = await deps.upload(imageValue, context);
        const url = media.sourceUrl ?? media.source_url;
        if (!url) throw new Error('Uploaded media did not return a source URL');
        Object.assign(image, {
          ...media,
          sourceUrl: url,
          source_url: url,
          image: url,
          thumbnail: url,
          failUpload: false,
          error: null,
          isUploading: false,
          pendingUpload: false,
        });
      }
    } catch (error) {
      return { blockIndex, error };
    }
  }
  return null;
}
